import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MonitorPlay, Globe, ExternalLink, Info, Star } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Overline } from "../components/motion";
import { WorkCard } from "../components/tvui";
import { useTranslate } from "../context/TranslateContext";
import { getPlatform, seriesForPlatform } from "../mock/tv";

export default function PlatformProfile() {
  const { slug } = useParams();
  const { t } = useTranslate();
  const p = getPlatform(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!p) return (<div><InnerNav /><div className="p-20 text-center font-body text-ash">{t("Platform not found.")}</div></div>);
  const catalogue = seriesForPlatform(slug);
  const listenText = `${p.name}, a streaming platform available in ${p.countries.join(", ")}.`;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${p.accent}22 0%, #FBF6EC 62%)` }}>
      <InnerNav />
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex h-28 w-40 shrink-0 items-center justify-center rounded-3xl border" style={{ background: p.accent + "22", borderColor: p.accent + "66" }}>
            <span className="font-display text-3xl font-semibold" style={{ color: p.accent }}>{p.name}</span>
          </div>
          <div>
            <Overline style={{ color: p.accent }}>{t("Streaming Platform")}</Overline>
            <h1 data-testid="platform-name" className="mt-2 font-display text-5xl font-semibold tracking-tight text-cream md:text-6xl">{p.name}</h1>
            {p.former?.length > 0 && <div className="mt-1 font-body text-sm text-ash">{t("Formerly")}: {p.former.join(", ")}</div>}
            <div className="mt-2 flex flex-wrap items-center gap-4 font-body text-sm text-ash">
              <span className="flex items-center gap-1.5"><Globe className="h-4 w-4" />{p.countries.join(", ")}</span>
              <span className="flex items-center gap-1.5"><MonitorPlay className="h-4 w-4" />{p.access.join(" · ")}</span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid="platform-official" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-body font-medium text-ink transition-transform hover:scale-105">{t("Official site")} <ExternalLink className="h-4 w-4" /></a>
            
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {p.languages.map((x) => <span key={x} className="rounded-full border border-edge px-4 py-1.5 font-body text-sm text-ash">{t(x)}</span>)}
        </div>

        <div className="mt-8 inline-flex items-center gap-2 rounded-xl border border-saffron/30 bg-saffron/10 px-4 py-2 font-body text-sm text-saffron" data-testid="platform-demo-note">
          <Info className="h-4 w-4" /> {t("Demo catalogue — availability is illustrative and not a live or exhaustive listing.")}
        </div>

        <h2 className="mt-14 mb-6 font-display text-3xl font-semibold text-cream">{t("On")} {p.name}</h2>
        {catalogue.length > 0 ? (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">{catalogue.map((s, i) => <WorkCard key={s.slug} s={s} i={i} />)}</div>
        ) : <p className="font-body text-ash" data-testid="platform-no-titles">{t("No titles catalogued yet.")}</p>}
      </div>
    </div>
  );
}
